import React, { useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { apiGet, apiPost } from '../../../lib/shopperApi';

const emptyForm = {
  label: 'Home',
  first_name: '',
  last_name: '',
  company: '',
  phone: '',
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
  is_default_billing: false,
  is_default_shipping: false,
};

const AddressBook = () => {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = async () => {
    setLoading(true);
    const { ok, data } = await apiGet('/addresses');
    if (ok && Array.isArray(data?.addresses)) setAddresses(data.addresses);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startAdd = () => {
    setForm({ ...emptyForm, is_default_billing: addresses.length === 0, is_default_shipping: addresses.length === 0 });
    setEditingId(null);
    setError(null); setFlash(null);
    setShowForm(true);
  };

  const startEdit = (a) => {
    setForm({
      label:        a.label || 'Home',
      first_name:   a.first_name  || '',
      last_name:    a.last_name   || '',
      company:      a.company     || '',
      phone:        a.phone       || '',
      address_1:    a.address_1   || '',
      address_2:    a.address_2   || '',
      city:         a.city        || '',
      state:        a.state       || '',
      postal_code:  a.postal_code || '',
      country:      a.country     || 'US',
      is_default_billing:  !!a.is_default_billing,
      is_default_shipping: !!a.is_default_shipping,
    });
    setEditingId(a.id);
    setError(null); setFlash(null);
    setShowForm(true);
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true); setError(null);
    const path = editingId ? `/addresses/${editingId}/update` : '/addresses';
    const { ok, data } = await apiPost(path, form);
    setBusy(false);
    if (!ok) { setError(data?.error || 'Could not save address.'); return; }
    if (Array.isArray(data?.addresses)) setAddresses(data.addresses);
    setShowForm(false);
    setFlash(editingId ? 'Address updated.' : 'Address added.');
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    const { ok, data } = await apiPost(`/addresses/${id}/delete`, {});
    if (!ok) { setError(data?.error || 'Could not delete.'); return; }
    if (Array.isArray(data?.addresses)) setAddresses(data.addresses);
    setFlash('Address deleted.');
  };

  const setDefault = async (id, kind) => {
    const { ok, data } = await apiPost(`/addresses/${id}/set-default`, { kind });
    if (!ok) { setError(data?.error || 'Could not set default.'); return; }
    if (Array.isArray(data?.addresses)) setAddresses(data.addresses);
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground">Address Book</h2>
              <p className="text-sm text-muted-foreground mt-1">Manage your shipping and billing addresses</p>
            </div>
            {!showForm && (
              <Button onClick={startAdd} iconName="Plus" iconPosition="left">Add Address</Button>
            )}
          </div>
        </div>

        {(flash || error) && (
          <div className="px-6 pt-4">
            {flash && <p className="text-sm text-green-700">{flash}</p>}
            {error && <p className="text-sm text-red-700">{error}</p>}
          </div>
        )}

        {showForm && (
          <div className="px-6 py-6 bg-accent/10 border-b border-border">
            <div className="max-w-2xl">
              <h3 className="text-lg font-medium text-foreground mb-4">
                {editingId ? 'Edit Address' : 'Add New Address'}
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Label</label>
                  <Input value={form.label} onChange={(e) => setField('label', e.target.value)} placeholder="Home, Work, etc." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">First Name</label>
                  <Input value={form.first_name} onChange={(e) => setField('first_name', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Last Name</label>
                  <Input value={form.last_name} onChange={(e) => setField('last_name', e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Company (optional)</label>
                  <Input value={form.company} onChange={(e) => setField('company', e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Street Address *</label>
                  <Input value={form.address_1} onChange={(e) => setField('address_1', e.target.value)} placeholder="123 Main Street" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Apartment, Suite, etc.</label>
                  <Input value={form.address_2} onChange={(e) => setField('address_2', e.target.value)} placeholder="Apt 4B" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">City *</label>
                  <Input value={form.city} onChange={(e) => setField('city', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">State / Region</label>
                  <Input value={form.state} onChange={(e) => setField('state', e.target.value)} placeholder="NY" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Postal Code *</label>
                  <Input value={form.postal_code} onChange={(e) => setField('postal_code', e.target.value)} placeholder="10001" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Country (2-letter) *</label>
                  <Input value={form.country} onChange={(e) => setField('country', e.target.value.toUpperCase().slice(0,2))} maxLength={2} placeholder="US" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                  <Input type="tel" value={form.phone} onChange={(e) => setField('phone', e.target.value)} />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_default_shipping} onChange={(e) => setField('is_default_shipping', e.target.checked)} className="w-4 h-4 accent-primary rounded" />
                  Default for shipping
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.is_default_billing} onChange={(e) => setField('is_default_billing', e.target.checked)} className="w-4 h-4 accent-primary rounded" />
                  Default for billing
                </label>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6">
                <Button variant="ghost" onClick={() => setShowForm(false)} disabled={busy}>Cancel</Button>
                <Button onClick={save} iconName="Check" iconPosition="left" disabled={busy}>
                  {busy ? 'Saving…' : (editingId ? 'Update Address' : 'Add Address')}
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="p-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading addresses…</p>
          ) : addresses.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {addresses.map((a) => (
                <div
                  key={a.id}
                  className={cn(
                    'relative p-4 rounded-lg border transition-colors',
                    (a.is_default_shipping || a.is_default_billing)
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="font-semibold text-foreground">{a.label}</p>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {a.is_default_shipping && <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">Default shipping</span>}
                        {a.is_default_billing && <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-medium">Default billing</span>}
                      </div>
                    </div>
                  </div>

                  <address className="not-italic text-sm text-muted-foreground space-y-1 mb-4">
                    {(a.first_name || a.last_name) && <p className="font-medium text-foreground">{[a.first_name, a.last_name].filter(Boolean).join(' ')}</p>}
                    {a.company && <p>{a.company}</p>}
                    <p>{a.address_1}</p>
                    {a.address_2 && <p>{a.address_2}</p>}
                    <p>{[a.city, a.state].filter(Boolean).join(', ')} {a.postal_code}</p>
                    <p className="uppercase">{a.country}</p>
                    {a.phone && <p>{a.phone}</p>}
                  </address>

                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    {!a.is_default_shipping && (
                      <button onClick={() => setDefault(a.id, 'shipping')} className="text-primary hover:underline">Use for shipping</button>
                    )}
                    {!a.is_default_billing && (
                      <button onClick={() => setDefault(a.id, 'billing')} className="text-primary hover:underline">Use for billing</button>
                    )}
                    <span className="flex-1" />
                    <Button variant="ghost" size="sm" onClick={() => startEdit(a)} iconName="Edit">Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(a.id)} iconName="Trash" className="text-red-600 hover:text-red-700">Delete</Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="MapPin" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No addresses saved</h3>
              <p className="text-sm text-muted-foreground mb-6">Add your first address to make checkout faster</p>
              <Button onClick={startAdd}>Add Your First Address</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddressBook;
